import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  lazy,
  Suspense
} from 'react'
import { composeClasses } from '@/utils/compose-classes'
import { formatPhoneNumber } from '@/utils/formatters'
import { InputField } from './components'
import type { InputProps } from './interfaces/types'

const PhoneCodesController = lazy(
  () => import('./components/phone-codes-controller/PhoneCodesController')
)

const InfoLabel = lazy(() => import('./components/info-label/InfoLabel'))

const Label = lazy(() => import('./components/label/Label'))

const LeftIcon = lazy(() => import('./components/icons/left-icon/LeftIcon'))

const RightIcon = lazy(() => import('./components/icons/right-icon/RightIcon'))

const RightText = lazy(() => import('./components/right-text/RightText'))

export const Input = forwardRef(
  (
    {
      labelText,
      className,
      error,
      information,
      name,
      informationIcon = 'error',
      leftIcon,
      leftIconClass,
      rightIconClass,
      rightIcon,
      rightText,
      disabled,
      type,
      inputMode,
      placeholder,
      onChange,
      onRightIconClick,
      getRef,
      onPhoneCodeChange,
      value,
      classNameContainer,
      phoneCodesIsResponsive,
      classNameLabel,
      classNameInput,
      informationIconClass,
      width = '',
      height = '',
      labelPosition = {
        top: 16,
        left: 16
      },
      hideLabel = false,
      onBlur,
      ...props
    }: InputProps,
    _ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)

    const [active, setActive] = useState(false)
    const [selectedCode, setSelectedCode] = useState('+52')

    const handleActivation = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setActive(!!e.target?.value)
    }, [])

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation()
        const input = event?.target?.value
        const formattedValue = formatPhoneNumber(input)
        if (inputRef?.current) {
          inputRef.current.value = formattedValue
        }
      },
      []
    )

    const focusInput = useCallback(() => {
      inputRef?.current?.focus()
    }, [])

    const handleLabelClick = useCallback(() => {
      if (inputRef?.current) {
        focusInput()
      }
    }, [focusInput])

    //* ObserversK
    useEffect(() => {
      if (inputRef && getRef) {
        getRef(inputRef)
      }
    }, [inputRef, getRef])

    return (
      <div
        className={composeClasses(width, height, classNameContainer)}
        ref={_ref}
        data-testid="text-field-container"
      >
        <label
          className={composeClasses(
            error && 'shake-horizontal',
            'relative inline-flex justify-center w-full items-center',
            className
          )}
          htmlFor={name}
          data-testid="text-field-label"
        >
          <InputField
            inputMode={inputMode}
            disabled={disabled}
            error={error}
            handleActivation={handleActivation}
            handleInputChange={handleInputChange}
            inputRef={inputRef}
            labelText={labelText}
            hideLabel={hideLabel}
            onBlur={onBlur}
            leftIcon={leftIcon}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            props={props}
            rightIcon={rightIcon}
            rightText={rightText}
            type={type}
            value={value || ''}
            className={classNameInput}
            height={height}
          />

          {type === 'phone-number' && (
            <Suspense>
              <PhoneCodesController
                isResponsive={phoneCodesIsResponsive}
                disabled={disabled}
                focusInput={focusInput}
                onPhoneCodeChange={onPhoneCodeChange}
                setSelectedCode={setSelectedCode}
                selectedCode={selectedCode}
              />
            </Suspense>
          )}

          {!hideLabel && (
            <Suspense>
              <Label
                active={active || !!value}
                disabled={disabled}
                error={error}
                position={labelPosition}
                handleLabelClick={handleLabelClick}
                labelText={labelText}
                leftIcon={leftIcon}
                name={name}
                value={value}
                placeholder={placeholder}
                type={type}
                className={classNameLabel}
                height={height}
              />
            </Suspense>
          )}

          {leftIcon && (
            <Suspense>
              <LeftIcon
                leftIcon={leftIcon}
                leftIconClass={composeClasses(
                  'text-[1.5rem] text-primary',
                  leftIconClass
                )}
                type={type}
                disabled={disabled}
              />
            </Suspense>
          )}

          {rightText && (
            <Suspense>
              <RightText rightText={rightText} rightIcon={rightIcon} />
            </Suspense>
          )}

          {rightIcon && (
            <Suspense>
              <RightIcon
                rightIcon={rightIcon}
                rightIconClass={composeClasses(
                  'text-[1.5rem] right-4 text-primary',
                  rightIconClass
                )}
                disabled={disabled}
                error={error}
                onRightIconClick={(event) => onRightIconClick?.(event)}
              />
            </Suspense>
          )}
        </label>

        {(information || error) && (
          <Suspense>
            <InfoLabel
              information={information}
              error={error}
              informationIcon={informationIcon}
              disabled={disabled}
              informationIconClass={informationIconClass}
            />
          </Suspense>
        )}
      </div>
    )
  }
)

export default Input
